/**
 * PAROLA KURMA SAYFASI
 *
 * Kimlik ISTEMEZ — isteyemez, kullanicinin daha parolasi yok. Yetkiyi
 * adresteki tek kullanimlik jeton tasir.
 *
 * Bu sayfanin varlik sebebi: parolayi kimse TASIMASIN. Operator uretip
 * WhatsApp'tan yollamasin, bot Telegram'a yazmasin, e-postaya dusmesin.
 * Kutunun sahibi buraya gelir ve parolasini KENDISI koyar; boylece parola
 * hicbir sohbette, hicbir gelen kutusunda iz birakmaz.
 */
import { useEffect, useState, type FormEvent } from "react";
import { api, ApiHatasi } from "../api";
import { Bildirim } from "../ui";
import { Logo, UrunAdi } from "../marka";

type Durum =
  | { hal: "yukleniyor" }
  | { hal: "gecerli"; eposta: string; amac: string; enAzParola: number }
  | { hal: "gecersiz"; sebep: "yok" | "suresi_doldu" | "kullanildi" }
  | { hal: "bitti"; eposta: string; webmail: string }
  | { hal: "kirik"; mesaj: string };

const SEBEP_METNI: Record<string, { baslik: string; aciklama: string }> = {
  yok: {
    baslik: "Bağlantı geçersiz",
    aciklama:
      "Adres eksik ya da hatalı kopyalanmış olabilir. Bağlantıyı size ileten kişiden yenisini isteyin.",
  },
  suresi_doldu: {
    baslik: "Bağlantının süresi dolmuş",
    aciklama:
      "Güvenlik gereği kurulum bağlantıları kısa ömürlüdür. Yöneticinizden yeni bir bağlantı isteyin; birkaç saniyede üretilir.",
  },
  kullanildi: {
    baslik: "Bu bağlantı zaten kullanılmış",
    aciklama:
      "Parolanız daha önce kurulmuş olabilir. Kurulduysa doğrudan giriş yapın; siz kurmadıysanız hemen yöneticinize haber verin.",
  },
};

export function ParolaKur({ jeton }: { jeton: string }) {
  const [durum, setDurum] = useState<Durum>({ hal: "yukleniyor" });
  const [parola, setParola] = useState("");
  const [tekrar, setTekrar] = useState("");
  const [gorunur, setGorunur] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);

  // useCek kullanilmadi: bu sayfa cekimden SONRA form durumu tutuyor ve
  // gonderim sonrasi bambaska bir hâle geciyor.
  useEffect(() => {
    let gecerli = true;
    api
      .kurulumLinkiDurum(jeton)
      .then((y) => {
        if (!gecerli) return;
        const v = y.veri;
        if (v.durum === "gecerli") {
          setDurum({
            hal: "gecerli",
            eposta: v.eposta!,
            amac: v.amac ?? "yeni",
            enAzParola: v.enAzParola ?? 10,
          });
        } else {
          setDurum({ hal: "gecersiz", sebep: v.durum as "yok" | "suresi_doldu" | "kullanildi" });
        }
      })
      .catch((h) => {
        if (!gecerli) return;
        setDurum({
          hal: "kirik",
          mesaj: h instanceof ApiHatasi ? h.message : "Bağlantı durumu okunamadı",
        });
      });
    return () => {
      gecerli = false;
    };
  }, [jeton]);

  async function gonder(e: FormEvent) {
    e.preventDefault();
    if (durum.hal !== "gecerli") return;
    setHata(null);

    if (parola.length < durum.enAzParola) {
      setHata(`Parola en az ${durum.enAzParola} karakter olmalı.`);
      return;
    }
    if (parola !== tekrar) {
      setHata("İki parola birbirini tutmuyor.");
      return;
    }

    setBekliyor(true);
    try {
      const y = await api.kurulumParolaKur(jeton, parola);
      // Parolayi bellekte tutmanin bir sebebi kalmadi
      setParola("");
      setTekrar("");
      setDurum({ hal: "bitti", eposta: y.veri.eposta, webmail: y.veri.webmail });
    } catch (h) {
      setHata(h instanceof ApiHatasi ? h.message : "Parola kurulamadı");
    } finally {
      setBekliyor(false);
    }
  }

  return (
    <div className="giris-sayfa">
      <div className="giris-kutu" style={{ maxWidth: 460 }}>
        <div className="marka">
          <Logo boyut={26} />
          <UrunAdi />
        </div>

        {durum.hal === "yukleniyor" && <p className="soluk">Bağlantı kontrol ediliyor…</p>}

        {durum.hal === "kirik" && <Bildirim tur="kotu">{durum.mesaj}</Bildirim>}

        {durum.hal === "gecersiz" && (
          <>
            <h1>{SEBEP_METNI[durum.sebep]?.baslik ?? "Bağlantı geçersiz"}</h1>
            <p className="soluk">{SEBEP_METNI[durum.sebep]?.aciklama}</p>
            <p style={{ marginTop: 18 }}>
              <a href="#/">Giriş sayfasına dön</a>
            </p>
          </>
        )}

        {durum.hal === "bitti" && (
          <>
            <h1>Parolanız kuruldu</h1>
            <Bildirim tur="iyi">
              <b>{durum.eposta}</b> kutusuna artık bu parolayla girebilirsiniz.
            </Bildirim>
            <p className="soluk" style={{ marginTop: 14 }}>
              Parolanızı kimseyle paylaşmayın. Yöneticiniz dâhil hiç kimse parolanızı
              göremez; unutursanız sıfırlanır, gösterilmez.
            </p>
            <p style={{ marginTop: 18 }}>
              <a href={durum.webmail} target="_blank" rel="noreferrer">
                Webmail'e git →
              </a>
            </p>
            <p className="soluk" style={{ marginTop: 10, fontSize: 13 }}>
              Telefon ya da Outlook kurulumu için panelde <b>E-posta kurulumu</b>{" "}
              bölümündeki ayarları kullanın.
            </p>
          </>
        )}

        {durum.hal === "gecerli" && (
          <>
            <h1>{durum.amac === "sifirlama" ? "Yeni parolanızı koyun" : "Parolanızı belirleyin"}</h1>
            <p className="soluk">
              <b>{durum.eposta}</b> kutusu için parola belirliyorsunuz. Bu bağlantı{" "}
              <b>tek kullanımlıktır</b>.
            </p>

            <form onSubmit={gonder}>
              <label>
                Parola
                <input
                  type={gorunur ? "text" : "password"}
                  value={parola}
                  onChange={(e) => setParola(e.target.value)}
                  autoComplete="new-password"
                  autoFocus
                  required
                />
              </label>
              <label>
                Parola (tekrar)
                <input
                  type={gorunur ? "text" : "password"}
                  value={tekrar}
                  onChange={(e) => setTekrar(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </label>

              <label
                style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 400 }}
              >
                <input
                  type="checkbox"
                  checked={gorunur}
                  onChange={(e) => setGorunur(e.target.checked)}
                  style={{ width: "auto", margin: 0 }}
                />
                Parolayı göster
              </label>

              <p className="soluk" style={{ fontSize: 13, marginTop: -4 }}>
                En az {durum.enAzParola} karakter. Uzunluk karmaşıklıktan daha
                değerlidir: üç dört kelimeden oluşan bir cümle hem güçlü hem akılda
                kalıcıdır.
              </p>

              {hata && <Bildirim tur="kotu">{hata}</Bildirim>}

              <button type="submit" disabled={bekliyor}>
                {bekliyor ? "Kuruluyor…" : "Parolayı kur"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
